<script setup lang="ts">
import { BADGE_KEY_INVOICE, countUnread, getInvoices } from '@shared/accounting';
import KgrAppearanceSwitches from '@shared/components/KgrAppearanceSwitches.vue';
import KgrBadgeCounter from '@shared/components/KgrBadgeCounter.vue';
import KgrIcon from '@shared/components/KgrIcon.vue';
import KgrMenuLanguageSwitch from '@shared/components/KgrMenuLanguageSwitch.vue';
import KgrProfileImage from '@shared/components/KgrProfileImage.vue';
import { getCurrentLocale } from '@shared/composables/useBatchedTranslation';
import { useLocalePath } from '@shared/composables/useLocalePath';
import { loadLocale } from '@shared/i18n';
import { useBadgeStore } from '@shared/stores/badge';
import { useOrganizationStore } from '@shared/stores/organization';
import type { User } from '@shared/stores/user';
import { useUserStore } from '@shared/stores/user';
import { computed, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';

// user may be null (logged out / white-label): the menu then shows only the language
// switch (Angular `@else` branch).
defineProps<{ user?: User | null }>();

const userStore = useUserStore();
const orgStore = useOrganizationStore();
const router = useRouter();
const route = useRoute();
const localePath = useLocalePath();

const badgeStore = useBadgeStore();

const familyUsers = computed(() => userStore.familyUsers);
const org = computed(() => orgStore.organization);

// Re-seed the invoice unread badge on every organization (re)load — mirrors Angular, where
// UserMenu subscribes to org$ and re-runs countUnread(getInvoices()) on each emission. Watching
// the `membersCanViewInvoices` flag alone left the badge stale across an in-SPA user switch
// (SwitchUserView re-runs initApp without a page reload, so the badge store survives): going from
// a user with an unread invoice to one without — both with the flag `true` — never changed the
// watched value, so the previous count leaked over. Watching the org object (new ref per load) fixes it.
watch(
    org,
    async (organization) => {
        if (!organization?.membersCanViewInvoices) {
            badgeStore.setCounter(BADGE_KEY_INVOICE, 0);
            return;
        }
        try {
            badgeStore.setCounter(BADGE_KEY_INVOICE, countUnread(await getInvoices()));
        } catch {
            badgeStore.setCounter(BADGE_KEY_INVOICE, 0);
        }
    },
    { immediate: true },
);

// "Meine Familie" dropdown is open by default (Angular: usersDropdownOpen = true).
const usersDropdownOpen = ref(true);
function toggleUserDropdown() {
    usersDropdownOpen.value = !usersDropdownOpen.value;
}

function profileSrc(u: User): string | null {
    if (!u.hasProfileImage) {
        return null;
    }
    return `${window.kgrDynamicConfig.apiHost}${u.profileImageApiUrl}`;
}

const locales = ['de', 'en', 'es', 'tr', 'uk'];

// Fall back to the locale from the URL while the router has not resolved the route yet
// (initial render).
const activeLocale = computed(() => (route.params.locale as string | undefined) ?? getCurrentLocale());

async function setLocale(localeId: string) {
    await loadLocale(localeId);
    const newPath = route.fullPath.replace(new RegExp(`^/${route.params.locale}/`), `/${localeId}/`);
    router.push(newPath);
}

async function logout() {
    // await: der Store faehrt zuerst die Logout-Callbacks (Push-Abmeldung) mit noch gueltigem Token.
    await userStore.logout();
    router.replace('/home/login');
}
</script>

<template>
    <div
        v-if="user"
    >
        <div class="kgr-region kgr-scrollable" style="max-height: 100dvh;">
            <div class="kgr-text kgr-text--centered kgr-row kgr-row--bigMargin">
                <div class="username kgr-headline kgr-headline--h3">
                    {{ user.fullName }}
                </div>
            </div>

            <nav class="kgr-verticalMenu">
                <!-- Multiple profiles (parent + children) → collapsible family list -->
                <template v-if="familyUsers.length > 1">
                    <button
                        type="button"
                        class="userDropdownToggle kgr-verticalMenu__item is-level-1 kgr-clickable"
                        @click="toggleUserDropdown"
                    >
                        <span class="kgr-verticalMenu__itemIcon"><KgrIcon
                            name="users"
                            :small="true"
                        /></span>
                        <span class="kgr-verticalMenu__itemLabel">{{ $t('Meine Familie') }}</span>
                        <KgrIcon
                            name="arrow-down"
                            class="kgr-verticalMenu__itemAddendum kgr-icon"
                        />
                    </button>
                    <div
                        class="kgr-collapsible"
                        :class="{ 'is-open': usersDropdownOpen }"
                    >
                        <div class="kgr-collapsible__content kgr-region kgr-region--small">
                            <RouterLink
                                v-for="u in familyUsers"
                                :key="u.id"
                                :to="`/account/${u.id}`"
                                class="kgr-verticalMenu__item"
                                active-class="is-active"
                            >
                                <span class="kgr-verticalMenu__itemIcon">
                                    <KgrProfileImage
                                        :src="profileSrc(u)"
                                        :gender="u.gender"
                                        size="s"
                                    />
                                </span>
                                <span class="kgr-verticalMenu__itemLabel">{{ u.fullName }}</span>
                            </RouterLink>
                        </div>
                    </div>
                </template>

                <!-- Single profile → "Mein Profil" -->
                <RouterLink
                    v-if="familyUsers.length === 1"
                    :to="`/account/${user.id}`"
                    class="mainUserProfileMenuItem kgr-verticalMenu__item is-level-1"
                    active-class="is-active"
                >
                    <span class="kgr-verticalMenu__itemIcon">
                        <KgrProfileImage
                            :src="profileSrc(user)"
                            :gender="user.gender"
                            size="s"
                        />
                    </span>
                    <span class="kgr-verticalMenu__itemLabel">{{ $t('Mein Profil') }}</span>
                </RouterLink>

                <!-- Invoices — unread badge (BADGE_KEY_INVOICE) seeded on org load. -->
                <RouterLink
                    v-if="org?.membersCanViewInvoices"
                    :to="localePath('accounting')"
                    class="kgr-verticalMenu__item is-level-1"
                    active-class="is-active"
                >
                    <span class="kgr-verticalMenu__itemIcon"><KgrIcon
                        name="calculate"
                        :small="true"
                    /></span>
                    <span class="kgr-verticalMenu__itemLabel">{{ $t('Rechnungen') }}</span>
                    <KgrBadgeCounter
                        :badge-key="BADGE_KEY_INVOICE"
                        class="kgr-verticalMenu__itemAddendum"
                    />
                </RouterLink>

                <RouterLink
                    :to="localePath('preferences/notifications')"
                    class="kgr-verticalMenu__item is-level-1"
                    active-class="is-active"
                >
                    <span class="kgr-verticalMenu__itemIcon"><KgrIcon
                        name="settings"
                        :small="true"
                    /></span>
                    <span class="kgr-verticalMenu__itemLabel">{{ $t('Einstellungen') }}</span>
                </RouterLink>
            </nav>

            <hr class="kgr-separator kgr-separator--light" />

            <div class="kgr-row">
                <label>
                    <div class="kgr-verticalMenu__itemHeader">{{ $t('Sprache') }}</div>
                    <KgrMenuLanguageSwitch
                        :locales="locales"
                        :active-locale="activeLocale"
                        @locale-changed="setLocale"
                    />
                </label>
            </div>

            <KgrAppearanceSwitches class="kgr-row kgr-row--bigMargin" />

            <RouterLink
                v-if="userStore.hasMultipleAccounts"
                :to="`/${activeLocale}/user/switch`"
                class="kgr-button kgr-button--accent kgr-button--block kgr-button--outline kgr-row"
            >
                <KgrIcon
                    name="users"
                    class="kgr-button__icon kgr-button__icon--left"
                />
                {{ $t('Benutzer wechseln') }}
            </RouterLink>
            <button
                type="button"
                class="kgr-button kgr-button--accent kgr-button--block kgr-button--outline"
                @click="logout"
            >
                <KgrIcon
                    name="door-exit"
                    class="kgr-button__icon kgr-button__icon--left"
                />
                {{ $t('Logout') }}
            </button>
        </div>
    </div>

    <!-- Logged out: language switch only (Angular `@else`) -->
    <div
        v-else
        class="kgr-region"
    >
        <nav class="kgr-verticalMenu">
            <label>
                <div class="kgr-verticalMenu__itemHeader">{{ $t('Sprache') }}</div>
                <KgrMenuLanguageSwitch
                    :locales="locales"
                    :active-locale="activeLocale"
                    @locale-changed="setLocale"
                />
            </label>
        </nav>
    </div>
</template>

<style scoped lang="scss">
.userDropdownToggle:focus {
    outline: none;
}

.mainUserProfileMenuItem {
    --kgr-verticalMenu-icon-background-color: transparent;
}
</style>
